import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";


/** Normalize a single question item to { id, question }. Handles object, string, or [key, value]. */
const toQuestion = (item, index) => {
  if (item == null) return null;
  const oneBased = index + 1;
  const defaultId = `Q${oneBased}`;
  if (typeof item === "string") return { id: defaultId, question: item };
  if (Array.isArray(item)) {
    const [k, v] = item;
    return { id: k != null && String(k).trim() !== "" ? String(k) : defaultId, question: v != null ? String(v) : "" };
  }
  if (typeof item === "object") {
    const id =
      item.id ?? item.question_id ?? item.open_question_id ?? defaultId;
    const question =
      item.question ?? item.question_text ?? item.text ?? item.q ?? "";
    return { id: String(id).trim() || defaultId, question: String(question) };
  }
  return null;
};

const getOpenQuestions = (artifact) => {
  if (!artifact || typeof artifact !== "object") return [];
  const byId = new Map();

  const collect = (src) => {
    if (!src) return;
    if (Array.isArray(src)) {
      src.forEach((item, i) => {
        const q = item != null ? toQuestion(item, i) : null;
        if (q && q.id) byId.set(String(q.id), { id: String(q.id), question: String(q.question ?? "") });
      });
      return;
    }
    if (typeof src === "object" && src !== null && !Array.isArray(src)) {
      if (Array.isArray(src.questions)) return collect(src.questions);
      if (Array.isArray(src.open_questions)) return collect(src.open_questions);
      Object.entries(src).forEach(([k, v], i) => {
        const q = v != null && typeof v === "object" && !Array.isArray(v)
          ? toQuestion({ id: k, ...v }, i)
          : { id: k, question: v != null ? String(v) : "" };
        if (q && q.id) byId.set(String(q.id), { id: String(q.id), question: String(q.question ?? "") });
      });
    }
  };

  [artifact.normalized?.open_questions, artifact.dev_spec?.open_questions, artifact.qa_plan?.open_questions, artifact.open_questions].forEach(collect);

  return Array.from(byId.values());
};

const OpenQuestionsForm = ({ ticket, artifact, onSaveAnswers }) => {
  const questions = getOpenQuestions(artifact);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const existing = ticket?.open_question_answers || {};
    const qs = getOpenQuestions(artifact);
    setAnswers((prev) => {
      const next = { ...prev };
      qs.forEach((q) => {
        if (existing[q.id] !== undefined) next[q.id] = existing[q.id];
        else if (next[q.id] === undefined) next[q.id] = "";
      });
      return next;
    });
  }, [ticket?.id, ticket?.open_question_answers, artifact]);

  const handleChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveAnswers = async () => {
    const payload = {};
    questions.forEach((q) => {
      const v = answers[q.id];
      if (v != null && String(v).trim() !== "") payload[q.id] = String(v).trim();
    });
    setSaving(true);
    try {
      await onSaveAnswers(payload);
    } finally {
      setSaving(false);
    }
  };
  if (questions.length === 0) {
    return (
      <div className="req-detail-section">
        <div className="req-open-questions-form">
          <h4>Open questions (need your input)</h4>
          <p style={{ margin: 0, color: "#888", fontSize: "0.875rem" }}>
            No open questions from the generator for this ticket. When the Dev Spec or Normalized requirement includes open questions, they will appear here so you can enter answers and then use &quot;Regenerate Dev & QA&quot;.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="req-detail-section">
      <div className="req-open-questions-form">
        <h4>Open questions (need your input)</h4>
        <p style={{ margin: "0 0 16px 0", color: "#888", fontSize: "0.875rem" }}>
          Answer these and save, then use &quot;Regenerate Dev & QA&quot; to refresh the spec and test cases.
        </p>
        {questions.map((q) => (
          <div key={String(q.id)} className="req-open-question-item">
            <label htmlFor={`oq-${q.id}`}>
              {q.id}: {q.question}
            </label>
            <textarea
              id={`oq-${q.id}`}
              value={answers[q.id] ?? ""}
              onChange={(e) => handleChange(q.id, e.target.value)}
              placeholder="Your answer..."
            />
          </div>
        ))}
        <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
          <Button
            variant="contained"
            onClick={handleSaveAnswers}
            disabled={saving}
            sx={{ textTransform: "none" }}
          >
            {saving ? "Saving…" : "Save answers"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OpenQuestionsForm;
